import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import MetricCard from 'src/sections/@dashboard/marketing/components/MetricCard';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import Iconify from 'src/components/Iconify';
import { CHART_TOOLTIP_STYLE } from 'src/design/tokens';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';

const COLORS = {
  instagram: '#E1306C',
  violet: '#7C3AED',
  likes: '#E1306C',
  comments: '#1877F2',
  saves: '#FBBC04',
  shares: '#34D399',
  male: '#3B82F6',
  female: '#EC4899',
  other: '#9CA3AF',
};

function formatNumber(num) {
  if (num == null) return '—';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

function formatPct(val) {
  if (val == null) return null;
  const sign = val >= 0 ? '+' : '';
  return `${sign}${val.toFixed(1)}%`;
}

function formatDateTick(value) {
  if (!value) return '';
  const parts = value.split('-');
  if (parts.length >= 3) return `${parts[2]}/${parts[1]}`;
  return value;
}

export default function InstagramTab({ data }) {
  const { t } = useTranslation('marketing');

  // useMemo precisa rodar em toda renderizacao, mesmo com data ainda nulo —
  // um early return antes deles muda a contagem de hooks entre renders (ver
  // mesmo fix em OverviewTab.js).
  const { summary, daily, demographics, online_followers, hashtags, top_posts, stories, reels } = data ?? {};

  const dailyChartData = useMemo(() => {
    if (!daily) return [];
    return daily.map((d) => ({
      ...d,
      date: d.date,
    }));
  }, [daily]);

  const genderData = useMemo(() => {
    if (!demographics?.gender) return [];
    const g = demographics.gender;
    return [
      { name: t('analytics.demographics.male'), value: g.male || 0, color: COLORS.male },
      { name: t('analytics.demographics.female'), value: g.female || 0, color: COLORS.female },
      { name: t('analytics.demographics.other'), value: g.other || 0, color: COLORS.other },
    ];
  }, [demographics, t]);

  // A Meta descontinuou audience_gender_age (o CRUZAMENTO idade x genero) e a
  // API atual so devolve as duas dimensoes separadas. age_gender chega vazio
  // do backend; usamos demographics.age quando existe.
  const ageData = useMemo(() => {
    if (demographics?.age?.length) {
      return demographics.age.map((d) => ({ range: d.label, total: d.value || 0 }));
    }
    return (demographics?.age_gender || []).map((d) => ({
      range: d.range,
      total: (d.male || 0) + (d.female || 0),
    }));
  }, [demographics]);

  const hourlyData = useMemo(() => {
    if (!online_followers?.by_hour) return [];
    return online_followers.by_hour.map((h) => ({
      hour: `${String(h.hour).padStart(2, '0')}h`,
      count: h.count,
    }));
  }, [online_followers]);

  if (!data) {
    return <MktLoading />;
  }

  const interactionLabels = {
    likes: t('analytics.instagram.interactions.likes'),
    comments: t('analytics.instagram.interactions.comments'),
    saves: t('analytics.instagram.interactions.saves'),
    shares: t('analytics.instagram.interactions.shares'),
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className={d.stats.grid}>
        <MetricCard
          title={t('analytics.instagram.metrics.followers')}
          value={formatNumber(summary?.followers)}
          growth={formatPct(summary?.followers_growth_pct)}
          icon="eva:people-outline"
          color="violet"
        />
        <MetricCard
          title={t('analytics.instagram.metrics.reach')}
          value={formatNumber(summary?.reach)}
          growth={formatPct(summary?.reach_growth_pct)}
          icon="eva:eye-outline"
          color="blue"
        />
        {/* A Meta trocou `impressions` por `views` em 21/04/2025 — o backend
            preenche a mesma chave, mas o rotulo precisa dizer a verdade. */}
        <MetricCard
          title={t('analytics.instagram.metrics.views')}
          value={formatNumber(summary?.views ?? summary?.impressions)}
          growth={formatPct(summary?.impressions_growth_pct)}
          icon="eva:bar-chart-outline"
          color="amber"
        />
        <MetricCard
          title={t('analytics.instagram.metrics.engagementRate')}
          value={summary?.engagement_rate != null ? `${summary.engagement_rate.toFixed(2)}%` : '—'}
          growth={formatPct(summary?.engagement_rate_growth_pct)}
          icon="eva:heart-outline"
          color="rose"
        />
        {/* profile_views e website_clicks foram removidas pela Meta. As
            substitutas reais sao accounts_engaged e profile_links_taps. */}
        <MetricCard
          title={t('analytics.instagram.metrics.accountsEngaged')}
          value={formatNumber(summary?.accounts_engaged ?? summary?.profile_views)}
          icon="eva:person-outline"
          color="indigo"
        />
        <MetricCard
          title={t('analytics.instagram.metrics.profileLinksTaps')}
          value={formatNumber(summary?.profile_links_taps ?? summary?.website_clicks)}
          icon="eva:external-link-outline"
          color="emerald"
        />
      </div>

      {/* Daily Reach & Impressions Chart */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>{t('analytics.instagram.charts.reachImpressions')}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailyChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip
              {...CHART_TOOLTIP_STYLE}
              formatter={(value, name) => [
                formatNumber(value),
                name === 'reach' ? t('analytics.instagram.metrics.reach') : t('analytics.instagram.metrics.views'),
              ]}
              labelFormatter={formatDateTick}
            />
            <Legend formatter={(v) => (v === 'reach' ? t('analytics.instagram.metrics.reach') : t('analytics.instagram.metrics.views'))} />
            <Line
              type="monotone"
              dataKey="reach"
              stroke={COLORS.instagram}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="impressions"
              stroke={COLORS.violet}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Daily Interactions Stacked Bar Chart */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>{t('analytics.instagram.charts.interactions')}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dailyChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip
              {...CHART_TOOLTIP_STYLE}
              formatter={(value, name) => [formatNumber(value), interactionLabels[name] || name]}
              labelFormatter={formatDateTick}
            />
            <Legend
              formatter={(v) => interactionLabels[v] || v}
            />
            <Bar dataKey="likes" stackId="interactions" fill={COLORS.likes} />
            <Bar dataKey="comments" stackId="interactions" fill={COLORS.comments} />
            <Bar dataKey="saves" stackId="interactions" fill={COLORS.saves} />
            <Bar dataKey="shares" stackId="interactions" fill={COLORS.shares} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Demographics */}
      <div className={d.charts.grid2}>
        {/* Gender Distribution */}
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>{t('analytics.instagram.demographics.gender')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={genderData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tickFormatter={formatNumber} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} width={80} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value) => [formatNumber(value), t('analytics.instagram.metrics.followers')]} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {genderData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Age Ranges */}
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>{t('analytics.instagram.demographics.ageRanges')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={ageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="range" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              {/* Barra unica: a Meta nao devolve mais a quebra por genero
                  DENTRO de cada faixa etaria, so os dois eixos separados. */}
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value) => [formatNumber(value), t('analytics.instagram.demographics.ageRanges')]} />
              <Bar dataKey="total" fill={COLORS.instagram} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Cities Table */}
      {demographics?.cities && demographics.cities.length > 0 && (
        <div className={d.table.container}>
          <h3 className={d.charts.title}>{t('analytics.instagram.cities.title')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={d.table.header}>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('analytics.instagram.cities.city')}
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('analytics.instagram.cities.followers')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {demographics.cities.map((city, idx) => (
                  <tr key={idx} className={d.table.row}>
                    <td className="py-3 px-4 text-sm text-gray-700">{city.name}</td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {formatNumber(city.count)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Hashtag Performance Table */}
      {hashtags && hashtags.length > 0 && (
        <div className={d.table.container}>
          <h3 className={d.charts.title}>{t('analytics.instagram.hashtags.title')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={d.table.header}>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('analytics.instagram.hashtags.tag')}
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('analytics.instagram.metrics.views')}
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('analytics.instagram.metrics.reach')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {hashtags.map((h, idx) => (
                  <tr key={idx} className={d.table.row}>
                    <td className="py-3 px-4 text-sm text-gray-700 font-medium">#{h.tag}</td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {formatNumber(h.impressions)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {formatNumber(h.reach)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Online Followers by Hour */}
      {hourlyData.length > 0 && (
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>{t('analytics.instagram.onlineFollowers')}</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="hour" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
              <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value) => [formatNumber(value), t('analytics.instagram.online')]} />
              <Bar dataKey="count" fill={COLORS.instagram} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stories Summary */}
      {stories && (
        <div>
          <h3 className={d.charts.title}>{t('analytics.instagram.stories.title')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-pink-50">
                <Iconify icon="eva:film-outline" width={20} className="text-pink-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(stories.total)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.stories.total')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                <Iconify icon="eva:eye-outline" width={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(stories.reach)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.stories.reach')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-50">
                <Iconify icon="eva:message-circle-outline" width={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(stories.replies)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.stories.replies')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-50">
                <Iconify icon="eva:log-out-outline" width={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(stories.exits)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.stories.exits')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reels Summary */}
      {reels && (
        <div>
          <h3 className={d.charts.title}>{t('analytics.instagram.reels.title')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-pink-50">
                <Iconify icon="eva:video-outline" width={20} className="text-pink-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(reels.total)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.stories.total')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                <Iconify icon="eva:play-circle-outline" width={20} className="text-violet-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(reels.plays)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.reels.plays')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-rose-50">
                <Iconify icon="eva:heart-outline" width={20} className="text-rose-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(reels.likes)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.interactions.likes')}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50">
                <Iconify icon="eva:share-outline" width={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{formatNumber(reels.shares)}</p>
                <p className="text-xs text-gray-500">{t('analytics.instagram.interactions.shares')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Posts Grid */}
      {top_posts && top_posts.length > 0 && (
        <div>
          <h3 className={d.charts.title}>{t('analytics.overview.topPosts')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
            {top_posts.map((post, idx) => (
              <div
                key={post.external_id || idx}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                {post.thumbnail ? (
                  <img
                    src={post.thumbnail}
                    alt=""
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <Iconify icon="eva:image-outline" width={40} className="text-gray-300" />
                  </div>
                )}
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-700 line-clamp-2">
                    {post.caption || t('analytics.noCaption')}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="capitalize">{post.type || 'post'}</span>
                    {post.published_at && (
                      <span>{new Date(post.published_at).toLocaleDateString('pt-BR')}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100">
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-900">{formatNumber(post.likes)}</p>
                      <p className="text-[10px] text-gray-400">{t('analytics.instagram.interactions.likes')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-900">{formatNumber(post.comments)}</p>
                      <p className="text-[10px] text-gray-400">{t('analytics.instagram.interactions.comments')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-900">{formatNumber(post.reach)}</p>
                      <p className="text-[10px] text-gray-400">{t('analytics.instagram.metrics.reach')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-900">{formatNumber(post.saves)}</p>
                      <p className="text-[10px] text-gray-400">{t('analytics.instagram.interactions.saves')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-900">{formatNumber(post.shares)}</p>
                      <p className="text-[10px] text-gray-400">{t('analytics.instagram.interactions.sharesShort')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-900">
                        {post.engagement_rate != null ? `${post.engagement_rate.toFixed(1)}%` : '—'}
                      </p>
                      <p className="text-[10px] text-gray-400">{t('analytics.instagram.interactions.engagement')}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
